import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../utils/api';
import { validators } from '../utils/validation';
import { toast } from './ui/sonner';
import AvailabilityManager from './AvailabilityManager';
import { flattenAddress, parseAddressString } from './AddressVerifier';
import {
  User, Settings, X, Bell, Lock, Star, Calendar, Filter,
} from 'lucide-react';

import JobAlertPreferences from './worker-profile/JobAlertPreferences';
import EditProfileSection from './worker-profile/EditProfileSection';
import ChangePasswordSection from './worker-profile/ChangePasswordSection';
import SecurityCenter from './security/SecurityCenter';
import NotificationsSection from './worker-profile/NotificationsSection';

const DEFAULT_AVAILABILITY = {
  monday: { available: true, start: '09:00', end: '17:00' },
  tuesday: { available: true, start: '09:00', end: '17:00' },
  wednesday: { available: true, start: '09:00', end: '17:00' },
  thursday: { available: true, start: '09:00', end: '17:00' },
  friday: { available: true, start: '09:00', end: '17:00' },
  saturday: { available: false, start: '09:00', end: '17:00' },
  sunday: { available: false, start: '09:00', end: '17:00' },
};

const DEFAULT_NOTIFICATIONS = {
  email_job_alerts: true,
  email_application_updates: true,
  email_messages: true,
  email_marketing: false,
  push_job_alerts: true,
  push_application_updates: true,
  push_messages: true,
  sms_urgent_alerts: false,
};

export default function WorkerProfileSettings({ user, onClose, onProfileUpdate }) {
  const [activeSection, setActiveSection] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    address: user?.address || '',
    skills: user?.skills?.join(', ') || '',
    certifications: user?.certifications?.join(', ') || '',
    experience_level: user?.experience_level || 'entry',
    hourly_rate: user?.hourly_rate || '',
    profile_photo: user?.profile_photo || '',
  });

  const [addressFields, setAddressFields] = useState(() => parseAddressString(user?.address || ''));
  const onAddressChange = (next) => {
    setAddressFields(next);
    setProfile((p) => ({ ...p, address: flattenAddress(next) }));
  };

  const [availability, setAvailability] = useState(DEFAULT_AVAILABILITY);

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS);

  // Personal shift-reminder override (iter77-B)
  const [reminderPref, setReminderPref] = useState({
    reminder_hours_before: null,
    reminder_enabled: true,
  });
  const [reminderSaving, setReminderSaving] = useState(false);

  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await apiCall('/api/worker/profile');
        if (response.profile) {
          const p = response.profile;
          setProfile({
            full_name: p.full_name || '',
            phone: p.phone || '',
            bio: p.bio || '',
            address: p.address || '',
            skills: p.skills?.join(', ') || '',
            certifications: p.certifications?.join(', ') || '',
            experience_level: p.experience_level || 'entry',
            hourly_rate: p.hourly_rate || '',
            profile_photo: p.profile_photo || '',
          });
          setAddressFields(parseAddressString(p.address || ''));
          if (p.availability) setAvailability(p.availability);
        }
        if (response.stats) setStats(response.stats);
      } catch {
        // silent
      }
    };

    const loadNotificationSettings = async () => {
      try {
        const response = await apiCall('/api/worker/notifications');
        if (response.settings) setNotifications(response.settings);
      } catch {
        // silent
      }
    };

    const loadReminderPreference = async () => {
      try {
        const r = await apiCall('/api/worker/reminder-preference');
        setReminderPref({
          reminder_hours_before: r.reminder_hours_before ?? null,
          reminder_enabled: r.reminder_enabled !== false,
        });
      } catch {
        // silent
      }
    };

    loadProfile();
    loadNotificationSettings();
    loadReminderPreference();
  }, []);

  const saveReminderPreference = async () => {
    setReminderSaving(true);
    try {
      await apiCall('/api/worker/reminder-preference', {
        method: 'PUT',
        body: JSON.stringify(reminderPref),
      });
      toast.success('Reminder preference saved!');
    } catch (e) {
      toast.error(e.message || 'Failed to save reminder preference');
    } finally {
      setReminderSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, or WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/worker/profile/photo`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        setProfile((prev) => ({ ...prev, profile_photo: data.file_url }));
        toast.success('Profile photo updated successfully!');
        onProfileUpdate?.({ ...user, profile_photo: data.file_url });
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    const profileErr =
      (profile.full_name ? validators.name(profile.full_name) : null) ||
      validators.phone(profile.phone) ||
      (profile.bio ? validators.bio(profile.bio) : null) ||
      (profile.hourly_rate ? validators.rate(profile.hourly_rate) : null);
    if (profileErr) {
      toast.error(profileErr);
      return;
    }
    setSaving(true);
    try {
      const response = await apiCall('/api/worker/profile', {
        method: 'PUT',
        body: JSON.stringify({ ...profile, availability }),
      });
      toast.success('Profile updated successfully!');
      onProfileUpdate?.(response.user);
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    const pwErr = validators.strongPassword(passwordForm.new_password);
    if (pwErr) {
      toast.error(pwErr);
      return;
    }

    setSaving(true);
    try {
      await apiCall('/api/worker/password', {
        method: 'PUT',
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        }),
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await apiCall('/api/worker/notifications', {
        method: 'PUT',
        body: JSON.stringify(notifications),
      });
      toast.success('Notification preferences saved!');
    } catch (error) {
      toast.error(error.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'profile', label: 'Edit Profile', icon: <User className="w-5 h-5" /> },
    { id: 'availability', label: 'Availability', icon: <Calendar className="w-5 h-5" /> },
    { id: 'job-alerts', label: 'Job Alerts', icon: <Filter className="w-5 h-5" /> },
    { id: 'password', label: 'Change Password', icon: <Lock className="w-5 h-5" /> },
    { id: 'security', label: 'Security', icon: <Lock className="w-5 h-5" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-5 h-5" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 p-4 hidden md:block">
          <div className="flex items-center gap-3 p-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Settings</h3>
              <p className="text-xs text-gray-500">Manage your account</p>
            </div>
          </div>

          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${
                  activeSection === section.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`settings-section-${section.id}`}
              >
                {section.icon}
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
          </nav>

          {stats && (
            <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-100">
              <p className="text-xs text-gray-500 mb-3">Your Stats</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Jobs Completed</span>
                  <span className="font-semibold text-gray-900">{stats.jobs_completed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Rating</span>
                  <span className="font-semibold text-gray-900 flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-500" />
                    {stats.average_rating || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Earned</span>
                  <span className="font-semibold text-emerald-600">${stats.total_earnings?.toFixed(0) || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              {sections.find((s) => s.id === activeSection)?.label}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              data-testid="close-settings-btn"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeSection === 'profile' && (
              <EditProfileSection
                profile={profile}
                setProfile={setProfile}
                addressFields={addressFields}
                onAddressChange={onAddressChange}
                photoInputRef={photoInputRef}
                uploadingPhoto={uploadingPhoto}
                onPhotoUpload={handlePhotoUpload}
                saving={saving}
                onSave={handleSaveProfile}
              />
            )}

            {activeSection === 'availability' && <AvailabilityManager />}

            {activeSection === 'job-alerts' && <JobAlertPreferences />}

            {activeSection === 'password' && (
              <ChangePasswordSection
                passwordForm={passwordForm}
                setPasswordForm={setPasswordForm}
                showPasswords={showPasswords}
                setShowPasswords={setShowPasswords}
                saving={saving}
                onSubmit={handleChangePassword}
              />
            )}

            {activeSection === 'security' && <SecurityCenter />}

            {activeSection === 'notifications' && (
              <NotificationsSection
                notifications={notifications}
                setNotifications={setNotifications}
                saving={saving}
                onSave={handleSaveNotifications}
                reminderPref={reminderPref}
                setReminderPref={setReminderPref}
                reminderSaving={reminderSaving}
                onSaveReminderPref={saveReminderPreference}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
